if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', class HeaderDrawer extends withAnimation(HTMLElement) {
    constructor() {
      super();

      this.isOpen = false;
      this.scrollLockEnable = true;
      this.bannersCloned = false;
      this.cloneBannersPromise = null;

      this.scheduleInit();
    }

    scheduleInit() {
      const init = () => this.initComponent();

      if ('requestIdleCallback' in window) {
        requestIdleCallback(init, { timeout: 1500 });
      } else {
        setTimeout(init, 0);
      }
    }

    initComponent() {
      this.header = document.querySelector('.section-header');
      this.mainDetailsToggle = this.querySelector('details');
      this.menuDrawer = this.querySelector('.menu-drawer');
      this.backDrop = this.querySelector('.menu-drawer__backdrop');

      this.addEventListener('keyup', this.onKeyUp.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));

      this.bindEvents();

      this.initAnimation({
        duration: 0.3
      });

      this.isRTL = document.documentElement.dir === 'rtl';

      DrawerManager.register(this);

      this.handleWindowResize = this.handleWindowResize.bind(this);
      window.addEventListener('resize', this.handleWindowResize);
      this.initBadges();
    }

    initBadges() {
      const badgesScript = document.getElementById('navigation-badges-data');
      if (!badgesScript) return;

      let badgesData = [];
      try {
        badgesData = JSON.parse(badgesScript.textContent.trim().replace(/,(\s*[}\]])/g, '$1'));
      } catch { return; }

      const menuLinks = this.querySelectorAll('.menu-drawer__menu-item');

      menuLinks.forEach(link => {
        const linkSpan = link.querySelector('span:not(.menu-badges__badge)');
        if (!linkSpan) return;

        const itemText = linkSpan.textContent.trim().toLowerCase();
        const matches = badgesData.filter(badge =>
          badge.items?.some(item => item.toLowerCase() === itemText)
        ).slice(0, 2);

        if (!matches.length || link.querySelector('.menu-badges')) return;
        const badgesHTML = matches.map(badge =>
          `<span class="menu-badges__badge"${badge.style ? ` style="${badge.style}"` : ''}>${badge.text}</span>`
        ).join('');

        link.insertAdjacentHTML('afterbegin', `<div class="menu-badges">${badgesHTML}</div>`);
      });
    }

    handleWindowResize() {
      if (!window.DeviceUtils.isMobile && this.isOpen) {
        this.forceClose();
      }
    }

    bindEvents(element = this) {
      element.querySelectorAll('summary').forEach((summary) =>
        summary.addEventListener('click', this.onSummaryClick.bind(this))
      );
      element.querySelectorAll(
        'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
      ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));

      if (element === this && this.backDrop) {
        this.backDrop.addEventListener('click', this.handleBackdropClick.bind(this));
      }
    }

    queueCloneBannersAfterOpen() {
      if (!window.DeviceUtils.isMobile || this.bannersCloned || this.cloneBannersPromise) return;

      clearTimeout(this.cloneBannersTimeout);

      this.cloneBannersTimeout = setTimeout(() => {
        if (!this.isOpen || !window.DeviceUtils.isMobile) return;
        this.cloneBannersMenu();
      }, 600);
    }

    cloneBannersMenu() {
      if (this.bannersCloned) return Promise.resolve();
      if (this.cloneBannersPromise) return this.cloneBannersPromise;

      this.cloneBannersPromise = new Promise((resolve) => {
        const megaMenus = document.querySelectorAll('.mega-menu__content[data-handle]');
        let pending = megaMenus.length;

        if (!pending) {
          this.bannersCloned = true;
          this.cloneBannersPromise = null;
          resolve();
          return;
        }

        const finish = () => {
          pending -= 1;
          if (pending === 0) {
            this.bannersCloned = true;
            this.cloneBannersPromise = null;
            resolve();
          }
        };

        megaMenus.forEach((megaMenu) => {
          const mediaHeight = megaMenu.querySelector('.mega-menu__grid')?.dataset.mediaHeight;
          const handle = megaMenu.getAttribute('data-handle');

          if (!handle) {
            finish();
            return;
          }

          let submenu = this.querySelector(`[data-handle="${handle}"].menu-drawer__submenu`);

          if (!submenu) {
            const linkElement = this.querySelector(`a[data-handle="${handle}"]`);
            if (linkElement) {
              submenu = this.createSubmenu(linkElement, handle);
            }
          }

          if (!submenu) {
            finish();
            return;
          }

          const menuList = submenu.querySelector('ul.menu-drawer__menu');

          if (!menuList) {
            finish();
            return;
          }

          let menuListLength = menuList.querySelectorAll('li').length + 1;

          if (megaMenu.classList.contains('disable-nested-menu')) {
            submenu.classList.add('disable-nested-menu');
            menuList.innerHTML = '';
            menuListLength = 1;
          }

          const menuItems = megaMenu.querySelectorAll('.mobile-menu-data');
          const columns = megaMenu.querySelectorAll('.mega-menu__column');

          if (!menuItems.length && !columns.length) {
            finish();
            return;
          }

          columns.forEach((column) => {
            const isMenuColumn = column.querySelector('.list-menu__column');
            let isFirstItemInColumn = true;

            if (isMenuColumn) {
              const menuHeading = column.querySelector('.list-menu__column > li > a.mega-menu__link--level-2');

              if (menuHeading) {
                const headingItem = document.createElement('li');
                headingItem.style.setProperty('--i', menuListLength++);

                if (isFirstItemInColumn) {
                  headingItem.classList.add('menu-drawer__menu-column');
                  isFirstItemInColumn = false;
                }

                const clonedLink = menuHeading.cloneNode(true);
                clonedLink.classList.remove('mega-menu__link');
                clonedLink.classList.add('menu-drawer__menu-item', 'menu-drawer__menu-item--level-2', 'tap-highlight-lg');

                headingItem.appendChild(clonedLink);
                menuList.appendChild(headingItem);
              }

              const listMenuItems = column.querySelectorAll('.list-menu__column .list-menu .mega-menu__link--level-3');

              listMenuItems.forEach((item) => {
                const listItem = document.createElement('li');
                listItem.style.setProperty('--i', menuListLength++);

                if (isFirstItemInColumn) {
                  listItem.classList.add('menu-drawer__menu-column');
                  isFirstItemInColumn = false;
                }

                const clonedItem = item.cloneNode(true);
                clonedItem.classList.remove('mega-menu__link', 'mega-menu__link--level-3', 'text-size-2');
clonedItem.classList.add('menu-drawer__menu-item', 'menu-drawer__menu-item--level-2', 'text-size-2', 'tap-highlight-lg');

                listItem.appendChild(clonedItem);
                menuList.appendChild(listItem);
              });
            } else if (column.hasAttribute('data-mobile-custom-html')) {
              const customHtmlItem = document.createElement('li');

              customHtmlItem.classList.add(
                'menu-drawer__menu-column',
                'menu-drawer__custom-html'
              );

              customHtmlItem.style.setProperty('--i', menuListLength++);

              const customHtmlContent = document.createElement('div');
              customHtmlContent.classList.add('menu-drawer__custom-html-content');

              Array.from(column.childNodes).forEach(child => {
                customHtmlContent.appendChild(child.cloneNode(true));
              });

              customHtmlItem.appendChild(customHtmlContent);
              menuList.appendChild(customHtmlItem);

            } else {
              const bannerAlignmentClass = column.dataset.alignment === 'start' ? 'banner-content-start' : '';
              const heading = column.querySelector('.mega-menu__block-heading');
              const limit = parseInt(column.dataset.itemsLimit, 10);
              const banners = Array.from(column.querySelectorAll('.banner-item'))
                .slice(0, Number.isFinite(limit) ? limit : undefined);
              const viewAllLink = column.querySelector('.button-link.button--arrow');

              if (heading) {
                const headingItem = document.createElement('li');
                headingItem.classList.add('mega-menu-heading-wrapper');

                if (isFirstItemInColumn) {
                  headingItem.classList.add('menu-drawer__menu-column');
                  isFirstItemInColumn = false;
                }

                headingItem.style.setProperty('--menu-media-height', `${mediaHeight}px`);
                headingItem.style.setProperty('--i', menuListLength++);
                headingItem.appendChild(heading.cloneNode(true));
                menuList.appendChild(headingItem);
              }

              banners.forEach((bannerItem) => {
                const bannerListItem = document.createElement('li');
                bannerListItem.classList.add('banner-item-wrapper');

                if (isFirstItemInColumn) {
                  bannerListItem.classList.add('menu-drawer__menu-column');
                  isFirstItemInColumn = false;
                }

                if (bannerAlignmentClass) {
                  bannerListItem.classList.add(bannerAlignmentClass);
                }

                bannerListItem.style.setProperty('--menu-media-height', `${mediaHeight}px`);
                bannerListItem.style.setProperty('--i', menuListLength++);

                const temp = document.createElement('div');
                temp.innerHTML = bannerItem.outerHTML;
                const clone = temp.firstElementChild;

                clone.querySelectorAll('img').forEach(img => {
                  if (img.srcset) {
                    img.dataset.srcset = img.srcset;
                    img.removeAttribute('srcset');
                  }
                  if (img.src) {
                    img.dataset.src = img.src;
                    img.removeAttribute('src');
                  }
                });

                bannerListItem.appendChild(clone);
                menuList.appendChild(bannerListItem);
              });

              if (viewAllLink) {
                const viewAllItem = document.createElement('li');
                viewAllItem.classList.add('banner-view-all-wrapper');
                viewAllItem.style.setProperty('--i', menuListLength++);
                viewAllItem.appendChild(viewAllLink.cloneNode(true));
                menuList.appendChild(viewAllItem);
              }
            }
          });

          this.bindEvents(submenu);
          finish();
        });
      });

      return this.cloneBannersPromise;
    }

    loadImages(container) {
      container.querySelectorAll('img[data-src]').forEach(img => {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      });

      container.querySelectorAll('img[data-srcset]').forEach(img => {
        img.srcset = img.dataset.srcset;
        img.removeAttribute('data-srcset');
      });
    }

    createSubmenu(linkElement, handle) {
      const template = document.getElementById('submenu-template');
      if (!template) return null;

      const clone = template.content.cloneNode(true);

      const titleSpan = linkElement.querySelector('span:not(.menu-badges__badge)');
      const originalText = (titleSpan || linkElement.querySelector('span'))?.textContent ?? '';
      const originalBadges = linkElement.querySelector('.menu-badges');
      const originalId = linkElement.id;
      const originalHref = linkElement.getAttribute('href');

      const details = clone.querySelector('details');
      details.id = `Details-menu-drawer-${handle}`;

      const summary = clone.querySelector('summary');
      summary.id = originalId;
      summary.setAttribute('aria-controls', `submenu-${handle}`);
      summary.querySelector('span:not(.menu-badges__badge)').textContent = originalText;

      if (originalBadges) {
        summary.insertBefore(originalBadges.cloneNode(true), summary.firstChild);
      }

      const submenuDiv = clone.querySelector('.menu-drawer__submenu');
      submenuDiv.id = `submenu-${handle}`;
      submenuDiv.setAttribute('data-handle', handle);

      const titlePlaceholder = clone.querySelector('.menu-drawer__menu-title-placeholder');
      let titleElement;

      if (originalHref && originalHref !== '#' && originalHref !== '') {
        titleElement = document.createElement('a');
        titleElement.href = originalHref;
        titleElement.className = 'menu-drawer__menu-title h3 hover-accent link-unstyled text-uppercase tap-highlight-lg';
      } else {
        titleElement = document.createElement('div');
        titleElement.className = 'menu-drawer__menu-title h3 text-uppercase';
      }

      titleElement.textContent = originalText;
      titlePlaceholder.replaceWith(titleElement);

      const parentElement = linkElement.parentNode;
      parentElement.replaceChild(clone, linkElement);

      this.bindEvents(details);

      return submenuDiv;
    }

    manageVideos(submenu, action) {
      const videos = submenu.querySelectorAll('deferred-media');

      videos.forEach(video => {
        const closestDetails = video.closest('details');
        if (closestDetails && closestDetails !== submenu) {
          if (!closestDetails.hasAttribute('open')) {
            return;
          }
        }

        if (action === 'load') {
          video.loadContent?.();
          video.playMedia?.();
        } else if (action === 'pause') {
          video.pauseMedia?.();
          video.restartMedia?.();
        }
      });
    }

    handleBackdropClick(event) {
      event.preventDefault();
      if (this.menuDrawer && !this.menuDrawer.classList.contains('country-selector-open')) {
        this.closeMenuDrawer(event, document.activeElement);
      }
    }

    onKeyUp(event) {
      if (event.code.toUpperCase() !== 'ESCAPE') return;

      const openDetailsElement = event.target.closest('details[open]');
      if (!openDetailsElement) return;

      openDetailsElement === this.mainDetailsToggle
        ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
        : this.closeSubmenu(openDetailsElement);
    }

    onFocusOut() {
      setTimeout(() => {
        if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
          this.closeMenuDrawer();
      });
    }

    setupTrapFocus(summaryElement, detailsElement) {
      const addTrapFocus = () => {
        trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
        summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
      };

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (!reducedMotion || !reducedMotion.matches) {
        summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      } else {
        addTrapFocus();
      }
    }

    openCatalog() {
      const stickyHeader = document.querySelector('sticky-header[data-sticky-enable-mobile]');
      const summary = this.mainDetailsToggle.querySelector('summary');

      if (stickyHeader) {
        stickyHeader.reveal();
        this.dataset.mode = 'catalog';
      } else {
        const header = document.querySelector('.section-header');
        const headerHeight = header?.offsetHeight || 0;
        const scrolledPastHeader = window.scrollY > headerHeight;
        requestAnimationFrame(() => {
          document.body.style.setProperty('--catalog-top-offset', `${headerHeight}px`);
          this.dataset.mode = scrolledPastHeader ? 'catalog-no-sticky' : 'catalog';
        });
      }

      summary?.click();
    }

    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentMenuElement = detailsElement.closest('.has-submenu');
      const isOpen = detailsElement.hasAttribute('open');

      if (detailsElement === this.mainDetailsToggle) {
        if (isOpen) event.preventDefault();
        isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);
      } else {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement?.classList.add('submenu-open');

        detailsElement.querySelector('.menu-drawer__inner-submenu').scrollTop = 0;
        if (parentMenuElement?.classList.contains('menu-drawer__menu')) {
          this.querySelector('.menu-drawer__navigation').scrollTop = 0;
        }

        this.manageVideos(detailsElement, 'load');
        this.loadImages(detailsElement);
        this.setupTrapFocus(summaryElement, detailsElement);
      }
    }

    openMenuDrawer(summaryElement) {
      this.isOpen = true;
      DrawerManager.setActiveWithSecondary(this);

      if (DrawerManager.diffrentSide()) {
        DrawerManager.clearSecondaryIfOpen(this);
      }

      DeviceUtils.updateHeaderSizes();
      this.header.classList.add('menu-open');
      this.resetSubmenus();
      document.body.classList.add('overflow-hidden');
      this.scrollLockEnable && scrollLock.lock();

      requestAnimationFrame(() => {
        this.animateShowContent(summaryElement);
      });
    }

    resetMenuState() {
      this.isOpen = false;
      this.mainDetailsToggle.removeAttribute('open');
      this.mainDetailsToggle.classList.remove('menu-opening');

      this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
      });

      this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
        submenu.classList.remove('submenu-open');
      });

      this.header.classList.remove('menu-open');

      this.querySelectorAll('[aria-expanded="true"]').forEach((element) => {
        if (element.closest('localization-form')) return;
        element.setAttribute('aria-expanded', 'false');
      });

      this.closeOtherPopup();

      DrawerManager.unsetActive(this);
    }

    closeMenuDrawer(event, elementToFocus = false) {
      if (!elementToFocus) return;
      removeTrapFocus(elementToFocus);
      this.closeAnimation(this.mainDetailsToggle);
      if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
    }

    forceClose() {
      this.resetMenuState();
      removeTrapFocus();
    }

    onCloseButtonClick(event) {
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }

    closeSubmenu(detailsElement) {
      const parentMenuElement = detailsElement.closest('.submenu-open');
      parentMenuElement && parentMenuElement.classList.remove('submenu-open');
      detailsElement.classList.remove('menu-opening');
      detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
      removeTrapFocus(detailsElement.querySelector('summary'));

      this.closeAnimation(detailsElement);
      if (parentMenuElement) parentMenuElement.querySelector('.menu-drawer__inner-submenu').scrollTop = 0;
    }

    closeAnimation(detailsElement) {
      if (detailsElement.classList.contains('menu-drawer-container')) {
        this.animateHideContent(detailsElement);
        return;
      }

      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          this.onCloseActions(detailsElement);
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }

    onShowActions(summaryElement) {
      this.mainDetailsToggle.classList.add('menu-opening');
      summaryElement.setAttribute('aria-expanded', true);
      trapFocus(this.mainDetailsToggle, summaryElement);
      DrawerManager.clearSecondaryIfOpen(this);
      this.queueCloneBannersAfterOpen();
    }

    onCloseActions(detailsElement) {
      detailsElement.removeAttribute('open');
      const parentDetails = detailsElement.closest('details[open]');
      this.closeOtherPopup();

      if (parentDetails) {
        trapFocus(parentDetails, detailsElement.querySelector('summary'));
      }

      this.manageVideos(detailsElement, 'pause');
    }

    async animateShowContent(summaryElement) {
      await this.animateSlideIn(this.menuDrawer, {
        slideDirection: 'left',
        backDrop: this.backDrop,
        onComplete: () => {
          this.onShowActions(summaryElement);
        }
      });
    }

    async animateHideContent(detailsElement) {
      await this.animateSlideOut(this.menuDrawer, {
        slideDirection: 'right',
        backDrop: this.backDrop,
        onComplete: () => {
          this.resetMenuState();
          this.onCloseActions(detailsElement);
          this.removeAttribute('data-mode');
        }
      });
    }

    resetSubmenus() {
      const openSubmenus = this.querySelectorAll('details[open], [aria-expanded="true"]');

      openSubmenus.forEach((submenu) => {
        this.manageVideos(submenu, 'pause');

        submenu.classList.remove('submenu-open');
        submenu.removeAttribute('open');

        const summary = submenu.querySelector('summary');
        if (summary) {
          summary.setAttribute('aria-expanded', 'false');
        }

        submenu.style.transition = 'none';
        submenu.offsetHeight;
        submenu.style.transition = '';
      });
    }

    closeOtherPopup() {
      const forms = document.querySelectorAll('localization-form');

      forms.forEach(form => {
        if (form.isOpen) form.forceClose();
      });

      const megaMenu = document.querySelector('mega-menu');

      if (megaMenu?.classList.contains('mega-menu--block')) {
        megaMenu.classList.remove('mega-menu--block');
        document.body.removeAttribute('data-mega-menu-block');
        megaMenu.forceClose();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleWindowResize);
    }
  });
}